/**
 * Enablement runtime flags — instrumentation opt-out and authoring mode (F-ED-01 / F-ED-03).
 *
 * Two layers: the build sets a default via env (`VITE_EDU_DISABLED`, `VITE_EDU_AUTHORING`); a user or
 * developer overrides it at runtime via localStorage (surfaced by the Help menu). localStorage wins
 * so a tester can flip either flag without a rebuild. All access is storage-safe (private-mode / SSR).
 */

const OPTOUT_KEY = 'ol.edu.optout';
const AUTHORING_KEY = 'ol.edu.authoring';

function envTrue(v: string | undefined): boolean {
  return v === 'true' || v === '1';
}

function lsGet(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/**
 * True when `edu.*` instrumentation must NOT emit. Telemetry is fully suppressed — no queue, no
 * network. Independent of UI enablement (tours + field help still work; only the event sink is off).
 */
export function instrumentationDisabled(): boolean {
  const ls = lsGet(OPTOUT_KEY);
  if (ls === 'true') return true;
  if (ls === 'false') return false;
  return envTrue(import.meta.env.VITE_EDU_DISABLED);
}

export function setInstrumentationOptOut(optOut: boolean): void {
  lsSet(OPTOUT_KEY, optOut ? 'true' : 'false');
}

/**
 * True when the missing-field-help authoring affordance should render ("no help, no publish"
 * legibility, §3.1). Off for normal analysts; on for content authors / dev so coverage gaps show.
 */
export function authoringMode(): boolean {
  const ls = lsGet(AUTHORING_KEY);
  if (ls === 'true') return true;
  if (ls === 'false') return false;
  return envTrue(import.meta.env.VITE_EDU_AUTHORING);
}

export function setAuthoringMode(on: boolean): void {
  lsSet(AUTHORING_KEY, on ? 'true' : 'false');
}
